import { useCallback, useEffect, useRef, useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import modalStyles from '@/components/ui/modals.module.css'
import {
  getDisclosureContent,
  getDisclosureTitle,
} from '@/features/applicant/pages/disclosureContent'
import { downloadDisclosurePdf } from '@/features/applicant/utils/disclosurePdf'
import type { DisclosureLinkId } from '@/features/applicant/types/pages'
import type { CreditBureauConfig } from '@/lib/api/applicationService'

interface SignatureDisclosureModalProps {
  linkId: DisclosureLinkId | null
  creditBureauConfig?: CreditBureauConfig | null
  isReadOnly?: boolean
  onClose: () => void
  onAccept: (linkId: DisclosureLinkId) => void
}

export function SignatureDisclosureModal({
  linkId,
  creditBureauConfig,
  isReadOnly = false,
  onClose,
  onAccept,
}: SignatureDisclosureModalProps) {
  const [scrollReachedBottom, setScrollReachedBottom] = useState(false)
  const [ackChecked, setAckChecked] = useState(false)
  const [isPdfDownloading, setIsPdfDownloading] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (linkId === null) return
    setScrollReachedBottom(false)
    setAckChecked(false)
    setIsPdfDownloading(false)
  }, [linkId])

  const checkReachedBottom = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    const noScrollNeeded = scrollHeight <= clientHeight + 2
    const atBottom = scrollTop + clientHeight >= scrollHeight - 20
    if (noScrollNeeded || atBottom) {
      setScrollReachedBottom(true)
    }
  }, [])

  const handleScroll = () => {
    if (scrollReachedBottom || isReadOnly) return
    checkReachedBottom()
  }

  useEffect(() => {
    if (linkId === null || isReadOnly) return
    let ro: ResizeObserver | null = null
    const runCheck = () => {
      requestAnimationFrame(() => checkReachedBottom())
    }
    const t = setTimeout(() => {
      runCheck()
      const el = scrollRef.current
      if (el) {
        ro = new ResizeObserver(runCheck)
        ro.observe(el)
      }
    }, 50)
    return () => {
      clearTimeout(t)
      ro?.disconnect()
    }
  }, [linkId, isReadOnly, checkReachedBottom])

  const handleAccept = () => {
    if (!linkId || !ackChecked) return
    onAccept(linkId)
  }

  const handleDownloadPdf = async () => {
    if (!isReadOnly || linkId === null || isPdfDownloading) return

    setIsPdfDownloading(true)
    try {
      await downloadDisclosurePdf(
        getDisclosureTitle(linkId),
        getDisclosureContent(linkId, creditBureauConfig),
      )
    } finally {
      setIsPdfDownloading(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={linkId !== null}
      title={linkId !== null ? getDisclosureTitle(linkId) : 'Disclosure document'}
      onClose={onClose}
      maxWidth="max-w-3xl"
      headerVariant="centeredTitle"
      bodyScrollOnly
      overlayDataTestId="signature-disclosure-modal"
      panelDataTestId="signature-disclosure-modal-panel"
      titleDataTestId="signature-disclosure-modal-title"
      closeButtonDataTestId="signature-disclosure-modal-close-button"
      bodyClassName="flex min-h-0 flex-col overflow-hidden p-0"
      footer={
        <div className="space-y-4">
          {!isReadOnly && !scrollReachedBottom ? (
            <p className="text-sm font-medium text-[#232946]">
              Please scroll down to the end of the document to continue.
            </p>
          ) : null}
          <div className={modalStyles.modalFooterActionGroup}>
            <button
              type="button"
              data-testid="signature-disclosure-modal-cancel-button"
              onClick={onClose}
              className={modalStyles.modalActionSecondary}
            >
              Cancel
            </button>
            {isReadOnly ? (
              <button
                type="button"
                data-testid="signature-disclosure-modal-download-button"
                onClick={handleDownloadPdf}
                disabled={isPdfDownloading}
                className={modalStyles.modalActionPrimary}
              >
                {isPdfDownloading ? (
                  <Loader2 className="mr-2 inline-block h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 inline-block h-4 w-4" />
                )}
                {isPdfDownloading ? 'Preparing PDF...' : 'Download PDF'}
              </button>
            ) : (
              <button
                type="button"
                data-testid="signature-disclosure-modal-accept-button"
                onClick={handleAccept}
                disabled={!ackChecked}
                className={modalStyles.modalActionPrimary}
              >
                Accept
              </button>
            )}
          </div>
        </div>
      }
    >
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="min-h-0 flex-1 overflow-y-auto bg-white p-6"
        data-testid="signature-disclosure-modal-body"
      >
        <style>{`
          .disclosure-modal-content a { color: #1597ff; text-decoration: underline; text-underline-offset: 2px; }
          .disclosure-modal-content a:hover { color: #0d7acc; }
          .disclosure-modal-content ul { list-style-type: disc; padding-left: 1.5rem; margin: 0.75rem 0; }
          .disclosure-modal-content ol { list-style-type: decimal; padding-left: 1.5rem; margin: 0.75rem 0; }
          .disclosure-modal-content li { display: list-item; margin: 0.35rem 0; line-height: 1.5; }
          .disclosure-modal-content h2 { font-size: 1.2rem; font-weight: 700; margin: 1.25rem 0 0.5rem; color: #0f172a; }
          .disclosure-modal-content h3 { font-size: 1.05rem; font-weight: 600; margin: 1rem 0 0.4rem; color: #1e293b; }
          .disclosure-modal-content p { margin: 0.5rem 0; line-height: 1.6; color: #334155; }
          .disclosure-modal-content strong { font-weight: 600; color: #1e293b; }
        `}</style>
        {linkId !== null ? (
          <div
            className="disclosure-modal-content text-sm text-slate-700 leading-relaxed"
            dangerouslySetInnerHTML={{
              __html: getDisclosureContent(linkId, creditBureauConfig),
            }}
          />
        ) : null}
        {!isReadOnly ? (
          <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3.5">
            <div className="flex items-start gap-3">
              <input
                data-testid="signature-disclosure-modal-ack-checkbox"
                type="checkbox"
                id="disclosure-ack"
                checked={ackChecked}
                onChange={(e) =>
                  setAckChecked(scrollReachedBottom ? e.target.checked : false)
                }
                disabled={!scrollReachedBottom}
                className="mt-0.5 h-5 w-5 flex-shrink-0 rounded border-slate-300 text-[#1597ff] focus:ring-2 focus:ring-[#1597ff]/40 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <label
                htmlFor="disclosure-ack"
                className={`text-sm font-medium ${!scrollReachedBottom ? 'text-slate-400' : 'cursor-pointer text-slate-700'}`}
              >
                I have read and acknowledge this document
              </label>
            </div>
          </div>
        ) : null}
      </div>
    </OnScreenModal>
  )
}
